/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { IChoiceGroupOptionStyles } from '@fluentui/react/lib/ChoiceGroup';
import { IPersonaStyles } from '@fluentui/react/lib/Persona';
import { IStackItemStyles, IStackStyles } from '@fluentui/react/lib/Stack';
import { ITheme } from '@fluentui/react/lib/Styling';
import { Theme } from '../../../../../common/enums/Theme';
import { ContrastColors } from '../../../../../styles/teams/TeamsColors';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { getHTMLDir } from '../../../../../utilities/Utils';

export const getLabelStyles = (): Partial<IStackStyles> => {
    return {
        root: {
            marginLeft: '2.4rem',
            marginRight: '2.4rem',
        },
    };
};

export const getStackItemStyles = (theme: ITheme): Partial<IStackItemStyles> => {
    return {
        root: {
            maxWidth: '28rem',
            selectors: {
                '&>span': {
                    color: theme.palette.neutralDark,
                    marginRight: '0.75rem',
                    marginLeft: '0.75rem',
                },
            },
        },
    };
};

export const getPersonaStyles = (): Partial<IPersonaStyles> => {
    return {
        root: {
            selectors: {
                '.ms-Persona-initials': {
                    borderRadius: '1.33px',
                },
            },
        },
    };
};

export const getChoiceGroupOptionStyles = (theme: ITheme, themeName: Theme): Partial<IChoiceGroupOptionStyles> => {
    const isRtl = getHTMLDir(getAppHost().getLocaleName()) === 'rtl';
    const circleDirStyles = isRtl ? { right: 0 } : { left: 0 };
    const pointDirStyles = isRtl ? { right: '5px' } : { left: '5px' };
    const circleTop = 6;
    const checkedTop = 11;
    const choiceOptionStyles =
        themeName === Theme.Contrast
            ? {
                  selectors: {
                      '.ms-ChoiceField-field::before': {
                          borderColor: ContrastColors.appBlack,
                          top: circleTop,
                          ...circleDirStyles,
                      },
                      '.ms-ChoiceField-field::after': {
                          top: checkedTop,
                          ...pointDirStyles,
                      },
                      '.ms-ChoiceField-field:hover::before': {
                          top: circleTop,
                          borderColor: ContrastColors.appBrand02,
                      },
                      '.ms-ChoiceField-field:focus::before': {
                          top: circleTop,
                          borderColor: ContrastColors.appBrand02,
                      },
                      '.ms-ChoiceField-field:hover::after': {
                          top: checkedTop,
                      },
                  },
              }
            : {
                  selectors: {
                      '.ms-ChoiceField-field::before': {
                          top: circleTop,
                          ...circleDirStyles,
                      },
                      '.ms-ChoiceField-field::after': {
                          top: checkedTop,
                          ...pointDirStyles,
                      },
                      '.ms-ChoiceField-field:hover::after': {
                          top: checkedTop,
                      },
                  },
              };
    return {
        root: {
            marginBottom: '1.25rem',
        },
        choiceFieldWrapper: choiceOptionStyles,
    };
};

export const getBodyStyles = (): Partial<IStackItemStyles> => {
    return {
        root: {
            height: '100%',
            overflowX: 'hidden',
            overflowY: 'auto',
            margin: '0 -1.75rem 1rem -1.75rem',
            padding: '0 1.75rem',
        },
    };
};
